<template>
    <common-modal 
        v-if="isVisible" 
        :show-header="true" 
        :show-footer="true" 
        title="🎉 Thông báo thanh toán" 
        size="modal-lg"
        @close="closeModal">
        
        <template #body>
            <div class="payment-notification-content">
                <!-- Header thông báo -->
                <div class="alert alert-success d-flex align-items-center mb-6">
                    <KTIcon icon-name="check-circle" icon-class="fs-2x text-success me-4" />
                    <div>
                        <h4 class="alert-heading mb-1">Có {{ receipts.length }} học sinh đã nộp tiền!</h4>
                        <p class="mb-0">Danh sách các phiếu thu đã được thanh toán thành công</p>
                    </div>
                </div>

                <!-- Danh sách phiếu thu -->
                <div class="receipt-list">
                    <div 
                        v-for="(receipt, index) in receipts" 
                        :key="receipt.id || index"
                        class="receipt-item card mb-4 shadow-sm">
                        
                        <div class="card-body">
                            <div class="row align-items-center">
                                <!-- Icon và thông tin chính -->
                                <div class="col-md-8">
                                    <div class="d-flex align-items-center">
                                        <div class="symbol symbol-50px me-4">
                                            <div class="symbol-label bg-light-success">
                                                <KTIcon icon-name="dollar" icon-class="fs-2x text-success" />
                                            </div>
                                        </div>
                                        
                                        <div class="flex-grow-1">
                                            <div class="d-flex align-items-center mb-2">
                                                <h5 class="mb-0 me-3">{{ receipt.name || 'Phiếu thu' }}</h5>
                                                <span class="badge badge-light-success">Đã thanh toán</span>
                                            </div>
                                            
                                            <div class="text-muted">
                                                <div class="d-flex align-items-center mb-1">
                                                    <KTIcon icon-name="code" icon-class="fs-6 me-2" />
                                                    <span class="fw-bold">Mã phiếu thu:</span>
                                                    <span class="ms-2">{{ receipt.code || 'N/A' }}</span>
                                                </div>
                                                
                                                <div class="d-flex align-items-center mb-1" v-if="receipt.paidDate">
                                                    <KTIcon icon-name="calendar" icon-class="fs-6 me-2" />
                                                    <span class="fw-bold">Ngày thanh toán:</span>
                                                    <span class="ms-2">{{ formatDateTime(receipt.paidDate) }}</span>
                                                </div>
                                                
                                                <div class="d-flex align-items-center" v-if="receipt.note">
                                                    <KTIcon icon-name="note" icon-class="fs-6 me-2" />
                                                    <span class="fw-bold">Ghi chú:</span>
                                                    <span class="ms-2">{{ receipt.note }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Số tiền -->
                                <div class="col-md-4 text-end">
                                    <div class="payment-amount">
                                        <div class="fs-2 fw-bold text-success">
                                            {{ formatCurrency(receipt.paid || receipt.amount || 0) }}
                                        </div>
                                        <div class="text-muted fs-7" v-if="receipt.amount && receipt.paid && receipt.amount !== receipt.paid">
                                            Tổng: {{ formatCurrency(receipt.amount) }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- Chi tiết phiếu thu nếu có -->
                            <div v-if="receipt.details && receipt.details.length > 0" class="mt-4">
                                <div class="separator separator-dashed my-3"></div>
                                <h6 class="text-gray-800 mb-3">Chi tiết:</h6>
                                <div class="row">
                                    <div 
                                        v-for="detail in receipt.details" 
                                        :key="detail.id"
                                        class="col-md-6 mb-2">
                                        <div class="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                                            <span class="text-gray-700">{{ detail.name }}</span>
                                            <span class="fw-bold text-gray-800">{{ formatCurrency(detail.amount || 0) }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        
        <template #footer>
            <button type="button" class="btn btn-success" @click="closeModal">
                <KTIcon icon-name="check" icon-class="fs-3 me-2" />
                Đã hiểu
            </button>
        </template>
    </common-modal>
</template>

<script setup lang="ts">
import { formatDateTime, formatNumber } from "~/core/helpers/util";
import type { ReceiptEntity } from "~/core/services/ApiClient";

interface Props {
    receipts: ReceiptEntity[];
    isVisible: boolean;
}

interface Emits {
    (e: 'close'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Helper function để format tiền tệ
const formatCurrency = (amount: number) => {
    return formatNumber(amount, " đ");
};

const closeModal = () => {
    emit('close');
};
</script>

<style scoped>
.payment-notification-content {
    max-height: 70vh;
    overflow-y: auto;
}

.receipt-item {
    border-left: 4px solid #50cd89;
    transition: all 0.3s ease;
}

.receipt-item:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1) !important;
}

.payment-amount {
    text-align: right;
}

.symbol-label {
    border-radius: 50%;
}

.receipt-list {
    max-height: 500px;
    overflow-y: auto;
    padding-right: 10px;
}

.receipt-list::-webkit-scrollbar {
    width: 6px;
}

.receipt-list::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
}

.receipt-list::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
}

.receipt-list::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}
</style> 